import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isAdmin } from '../lib/supabase';

interface DebugInfo {
  userEmail: string | null;
  userId: string | null;
  isAdminFromContext: boolean;
  isAdminFromFunction: boolean;
  sessionExists: boolean;
  jwtClaims: any;
  canInsertTest: boolean;
  errorMessage: string | null;
}

const AdminDebugPanel: React.FC = () => {
  const { user, isAdminUser } = useAuth();
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    userEmail: null,
    userId: null,
    isAdminFromContext: false,
    isAdminFromFunction: false,
    sessionExists: false,
    jwtClaims: null,
    canInsertTest: false,
    errorMessage: null
  });
  const [loading, setLoading] = useState(false);

  const runDebugCheck = async () => {
    setLoading(true);
    try {
      // 获取当前会话
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        setDebugInfo(prev => ({ ...prev, errorMessage: `Session错误: ${sessionError.message}` }));
        return;
      }

      // 检查管理员状态
      const adminFromFunction = user ? await isAdmin(user, true) : false;
      
      // 测试插入权限
      let canInsert = false;
      try {
        // 尝试执行一个不会实际插入数据的查询来测试权限
        const { error: insertError } = await supabase
          .from('insight_news_article')
          .insert({
            source_id: 999999, // 使用一个不存在的source_id
            title: 'TEST_TITLE_SHOULD_NOT_EXIST',
            summary: 'Test',
            content: 'Test',
            category: 'Test',
            publish_time: new Date().toISOString(),
            status: 0
          })
          .select()
          .limit(0); // 限制为0，不会实际插入
        
        canInsert = !insertError;
      } catch (error) {
        console.log('插入测试失败:', error);
      }

      setDebugInfo({
        userEmail: user?.email || null,
        userId: user?.id || null,
        isAdminFromContext: isAdminUser,
        isAdminFromFunction: adminFromFunction,
        sessionExists: !!session,
        jwtClaims: session?.access_token ? parseJWT(session.access_token) : null,
        canInsertTest: canInsert,
        errorMessage: null
      });
    } catch (error) {
      setDebugInfo(prev => ({ 
        ...prev, 
        errorMessage: `调试检查失败: ${error instanceof Error ? error.message : String(error)}` 
      }));
    } finally {
      setLoading(false);
    }
  };

  // 解析JWT token
  const parseJWT = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    runDebugCheck();
  }, [user, isAdminUser]);

  const testInsert = async () => {
    try {
      const { data, error } = await supabase
        .from('insight_news_article')
        .insert({
          source_id: 1,
          title: `测试文章 - ${new Date().toLocaleString()}`,
          summary: '这是一个测试文章摘要',
          content: '这是一个测试文章内容',
          category: '测试分类',
          publish_time: new Date().toISOString(),
          status: 0
        })
        .select();
      
      if (error) {
        alert(`插入失败: ${error.message}`);
      } else {
        alert('插入成功!');
        runDebugCheck(); // 重新检查状态
      }
    } catch (error) {
      alert(`插入异常: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  return (
    <div className="bg-gray-100 p-6 rounded-lg shadow-lg max-w-4xl mx-auto my-4">
      <h2 className="text-2xl font-bold mb-4 text-gray-800">管理员权限调试面板</h2>
      
      <div className="mb-4">
        <button 
          onClick={runDebugCheck}
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
        >
          {loading ? '检查中...' : '重新检查'}
        </button>
        
        <button 
          onClick={testInsert}
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
        >
          测试插入
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded shadow">
          <h3 className="font-semibold mb-2">用户信息</h3>
          <p><strong>邮箱:</strong> {debugInfo.userEmail || '未登录'}</p>
          <p><strong>用户ID:</strong> {debugInfo.userId || '无'}</p>
          <p><strong>会话存在:</strong> {debugInfo.sessionExists ? '✅ 是' : '❌ 否'}</p>
        </div>

        <div className="bg-white p-4 rounded shadow">
          <h3 className="font-semibold mb-2">管理员状态</h3>
          <p><strong>Context中的状态:</strong> {debugInfo.isAdminFromContext ? '✅ 是管理员' : '❌ 非管理员'}</p>
          <p><strong>函数检查结果:</strong> {debugInfo.isAdminFromFunction ? '✅ 是管理员' : '❌ 非管理员'}</p>
          <p><strong>插入权限测试:</strong> {debugInfo.canInsertTest ? '✅ 有权限' : '❌ 无权限'}</p>
        </div>

        {debugInfo.jwtClaims && (
          <div className="bg-white p-4 rounded shadow md:col-span-2">
            <h3 className="font-semibold mb-2">JWT Claims</h3>
            <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto">
              {JSON.stringify(debugInfo.jwtClaims, null, 2)}
            </pre>
          </div>
        )}

        {debugInfo.errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded md:col-span-2">
            <strong>错误:</strong> {debugInfo.errorMessage}
          </div>
        )}
      </div>

      <div className="mt-6 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
        <h4 className="font-semibold">预期结果:</h4>
        <ul className="list-disc list-inside mt-2">
          <li>邮箱应该是: yebaobao888@gmail.com</li>
          <li>管理员状态应该都是: ✅ 是管理员</li>
          <li>插入权限测试应该是: ✅ 有权限</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminDebugPanel;